import type { MouseEvent } from 'react'
import { namingConfig } from '@gap/utils/config/naming-config'
import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { themeConfig } from '@gap/shared/theme'
import GapAlert from 'reusable/mui/alert/GapAlert'
import GapChip from 'features/gap-components/status-chip/GapChip'
import { FYISyncModeOption, useAdvisorIntegrationFYIStore } from './AdvisorIntegrationFYIStore'

interface FYISyncModeOptionsProps {
   /** Which dialog's copy to show; the manage variant marks the current mode. */
   variant: 'connect' | 'manage'
}

/** The "what to sync" option cards; pressing one selects it. */
export default observer(function FYISyncModeOptions(props: FYISyncModeOptionsProps) {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()
   const options = props.variant === 'manage' ? advisorIntegrationFYIStore.manageSyncModeOptions : advisorIntegrationFYIStore.connectSyncModeOptions

   return <Stack spacing={1.5}>
      {options.map(option =>
         <FYISyncModeOptionCard
            key={option.value}
            option={option}
            selected={advisorIntegrationFYIStore.syncMode === option.value}
            onSelect={advisorIntegrationFYIStore.selectSyncModeHandler}
         />
      )}
   </Stack>
})

interface FYISyncModeOptionCardProps {
   option: FYISyncModeOption
   selected: boolean
   onSelect: (event: MouseEvent<HTMLElement>) => void
}

const FYISyncModeOptionCard = observer(function FYISyncModeOptionCard(props: FYISyncModeOptionCardProps) {
   const { option, selected } = props

   return <ButtonBase
      aria-pressed={selected}
      data-sync-mode={option.value}
      onClick={props.onSelect}
      sx={{
         display: 'flex',
         alignItems: 'flex-start',
         textAlign: 'left',
         width: '100%',
         p: 2,
         gap: 1.5,
         borderRadius: 4,
         border: `1px solid ${selected ? themeConfig.primary[800] : themeConfig.primary[200]}`,
         bgcolor: selected ? 'action.hover' : '#fff',
      }}
   >
      <SelectionIndicator selected={selected} />
      <Box sx={{ flex: 1 }}>
         <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1Bold">{option.title}</Typography>
            {option.badge && <GapChip colorVariant={option.badge.colorVariant} label={option.badge.label} />}
         </Stack>
         <Typography variant="body2" sx={{ color: themeConfig.primary[600], mt: 0.5 }}>{option.description}</Typography>
         <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {option.chips.map(chip => <Chip key={chip} label={chip} size="small" variant="outlined" />)}
         </Stack>
      </Box>
   </ButtonBase>
})

/** Draws the option's radio-style dot. */
function SelectionIndicator(props: { selected: boolean }) {
   return <Box sx={{
      width: 18,
      height: 18,
      mt: 0.25,
      flexShrink: 0,
      borderRadius: '50%',
      border: `${props.selected ? 5 : 1}px solid ${props.selected ? themeConfig.primary[800] : themeConfig.primary[400]}`,
      bgcolor: '#fff',
   }} />
}

/** Asks the user to acknowledge that FYI takes the client source over from XPM. */
export const FYISwitchSourceWarning = observer(function FYISwitchSourceWarning() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()

   return <GapAlert severity="warning" title="This switches your client source from XPM to FYI" sx={{ mt: 2 }}>
      <Typography variant="body2">
         Clients will stop syncing from Xero Practice Manager. Clients already in {namingConfig.product.singular} stay. FYI clients that match an XPM client are linked to it, and the rest are added.
      </Typography>
      <FormControlLabel
         sx={{ mt: 1, mr: 0 }}
         control={<Checkbox size="small" checked={advisorIntegrationFYIStore.switchAcknowledged} onChange={advisorIntegrationFYIStore.switchAcknowledgedChangeHandler} />}
         label={<Typography variant="body2">I understand, switch to FYI</Typography>}
      />
   </GapAlert>
})
